import type {
  ColoredMaterial,
  SalesOpportunity,
} from "@color-finder/color-finder-api/models";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { type Dispatch, type FC, useState } from "react";
import { useAppConfig } from "../../config/app-config";
import { logCustomEvent } from "../../datadog";
import { getAuthenticatedAxios } from "../../hooks/authentication";
import type {
  DigitalSample,
  DigitalSampleAsset,
} from "../../hooks/digital-samples";
import { reportCdpImagioCfWebBehaviour } from "../../service/cdp-report";
import { downloadAsset } from "../../service/download-asset";
import { attachDigitalSampleDownloadToOpportunity } from "../../service/sales-opportunity";
import {
  buildDigitalSampleTrackingData,
  DownloadAssetType,
  trackDownload,
} from "../../service/tracking";
import { getDataFromLocalstorageByKey } from "../../service/utils/get-data-localstorage";
import { DocumentationLink } from "../documentationLink/DocumentationLink";
import { OpportunityAutocomplete } from "../input/opportunityAutocomplete/OpportunityAutocomplete";
import { FullScreenLoader } from "../loader/FullScreenLoader";

/**
 * Props for {@link AxFDownloadDialog}.
 */
export interface AxFDownloadDialogProps {
  /** Controls dialog visibility. */
  open: boolean;
  /** Called when the user dismisses the dialog or the download completes. */
  onClose: Dispatch<any>;
  /** Colored material the AxF sample belongs to — used for tracking metadata. */
  material: ColoredMaterial;
  /** DSM digital sample that owns the AxF asset. */
  digitalSample: DigitalSample;
  /** AxF asset to download. The confirm button is disabled when absent. */
  axfFile?: DigitalSampleAsset;
  /** Receives the DOM id used by the hidden download anchor. */
  onChangeId?: (id: string) => void;
  /** Receives download-state transitions (e.g. `"success"`, `"error"`). */
  onDownloadState?: (result: string) => void;
}

/**
 * Confirmation dialog for AxF (X-Rite Appearance Exchange Format) downloads.
 *
 * On confirm it fires fire-and-forget `trackDownload` plus the
 * `digital-sample-download` and `axf-download` RUM actions, then kicks off
 * the browser download. Tracking is fired on user intent rather than on
 * fetch completion so cache hits, CORS/preflight failures, or expired
 * pre-signed URLs don't silently drop events.
 */
export const AxFDownloadDialog: FC<AxFDownloadDialogProps> = ({
  open,
  onClose,
  axfFile,
  material,
  digitalSample,
  onChangeId,
  onDownloadState,
}) => {
  const documentationLink = (
    <DocumentationLink
      document="digitalSampleSharing"
      label="legal requirements"
    />
  );
  const [acknowledged, setAcknowledged] = useState(false);
  const [opportunity, setOpportunity] = useState<SalesOpportunity>();
  const [loading, setLoading] = useState(false);
  const [config] = useAppConfig();

  const validated =
    (opportunity !== undefined || !config.salesOpportunities?.enabled) &&
    acknowledged;

  const onDownload = async () => {
    if (axfFile && validated) {
      const { axios } = getAuthenticatedAxios();
      setLoading(true);
      if (config.salesOpportunities?.enabled && opportunity) {
        await attachDigitalSampleDownloadToOpportunity(
          axios,
          opportunity,
          material,
          digitalSample,
          axfFile,
        );
        setLoading(false);
      }
      trackDownload(axios, {
        assetType: DownloadAssetType.AXF,
        digitalSample: buildDigitalSampleTrackingData(material, digitalSample),
        asset: {
          id: axfFile.id,
          fileName: axfFile.fileName,
          type: axfFile.type,
          tags: axfFile.tags,
        },
      });
      const colorCode =
        material.color.finalColorCode ?? material.color.preliminaryColorCode;
      logCustomEvent("digital-sample-download", {
        action: "download",
        digital_sample_id: digitalSample.id,
        digital_sample_asset_type: axfFile.type,
        material_grade: material.material.shortGrade,
        material_color_code: colorCode,
      });
      logCustomEvent("axf-download", {
        action: "download",
        digital_sample_id: digitalSample.id,
        digital_sample_asset_type: axfFile.type,
        material_grade: material.material.shortGrade,
        material_color_code: colorCode,
      });
      // CX-1130: external users only — presence of cdcUser in localStorage
      // is the same signal checkInternalUser() uses to segment audiences.
      if (getDataFromLocalstorageByKey("cdcUser")) {
        reportCdpImagioCfWebBehaviour({
          cov_web_act_type: "Click Link on Webpage",
          cov_primary_attribute: axfFile.fileName,
          cov_material: material.material.shortGrade,
          cov_color_code: colorCode,
          cov_uvp_number: material.availability.productNumber,
        });
      }
      downloadAsset(axfFile, onChangeId, onDownloadState, setLoading);
    }
    onClose(axfFile);
    setAcknowledged(false);
  };

  return (
    <>
      <FullScreenLoader open={loading} zIndex={1500} />
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>{axfFile?.fileName}</DialogTitle>
        <DialogContent>
          {config.salesOpportunities?.enabled && (
            <>
              <DialogContentText>
                Please provide a C4C Opportunity to link to your download.
                Downloads will be attached to the Opportunity, so you can
                retrieve them at a later point in time.
              </DialogContentText>
              <OpportunityAutocomplete
                value={opportunity}
                onChange={setOpportunity}
                required
                margin={"normal"}
              />
            </>
          )}
          <FormControlLabel
            sx={{ ml: 0 }}
            checked={acknowledged}
            onChange={() => setAcknowledged(!acknowledged)}
            control={<Checkbox />}
            label={
              <Typography lineHeight={1} variant={"body2"}>
                I will comply with the {documentationLink} when sharing Digital
                Samples.
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          <Button disabled={!validated} onClick={() => onDownload()}>
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
